import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Plus, Trash2, Edit2, Eye, EyeOff, X, Building2, GripVertical } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSET_TYPES = ["Multifamily", "Retail", "Industrial", "Mixed-Use", "Office", "Distressed", "Portfolio", "Other"];

const EMPTY = {
  title: "", city: "", state: "", asset_type: "Multifamily", units_sqft: "",
  price: "", closed_days: "", description: "", image_url: "", status: "closed",
  is_published: true, display_order: 0,
};

export default function AdminAcquisitionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // object or null
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/acquisitions`, { headers });
      setItems(res.data.acquisitions || []);
    } catch (err) { toast.error("Failed to load acquisitions"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/acquisitions/${editing.id}`, editing, { headers });
        toast.success("Acquisition updated");
      } else {
        await axios.post(`${API}/admin/acquisitions`, editing, { headers });
        toast.success("Acquisition added");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this acquisition? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/acquisitions/${id}`, { headers });
      toast.success("Deleted");
      load();
    } catch (err) { toast.error("Delete failed"); }
  };

  const togglePublish = async (a) => {
    try {
      await axios.put(`${API}/admin/acquisitions/${a.id}`, { ...a, is_published: !a.is_published }, { headers });
      load();
    } catch (err) { toast.error("Update failed"); }
  };

  const fieldCls = "w-full rounded-lg border border-[#e8e0d0] bg-white px-3 py-2 text-sm outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200";
  const labelCls = "mb-1 block text-[11px] font-bold uppercase tracking-wide text-[#888]";

  return (
    <div className="space-y-6" data-testid="admin-acquisitions">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0A0A0A]">Recent Acquisitions</h2>
          <p className="text-sm text-[#888]">Manage the track-record showcase shown on the public website.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY, display_order: items.length })} data-testid="add-acquisition-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-800">
          <Plus className="h-4 w-4" /> Add Acquisition
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#e8e0d0] py-16 text-center">
          <Building2 className="mx-auto h-10 w-10 text-[#ccc]" />
          <p className="mt-3 text-sm text-[#888]">No acquisitions yet. Add your first closed deal.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((a) => (
            <div key={a.id} className="overflow-hidden rounded-xl border border-[#e8e0d0] bg-white" data-testid={`admin-acq-${a.id}`}>
              <div className="relative h-36 bg-[#f5f0e6]">
                {a.image_url && <img src={a.image_url} alt={a.title} className="h-full w-full object-cover" />}
                <span className="absolute left-2 top-2 rounded bg-emerald-700 px-2 py-0.5 text-[10px] font-bold uppercase text-white">{a.asset_type}</span>
                {!a.is_published && <span className="absolute right-2 top-2 rounded bg-[#888] px-2 py-0.5 text-[10px] font-bold uppercase text-white">Hidden</span>}
              </div>
              <div className="p-3">
                <div className="flex items-center gap-1 text-[10px] text-[#aaa]"><GripVertical className="h-3 w-3" /> Order: {a.display_order}</div>
                <h3 className="mt-1 text-sm font-bold text-[#0A0A0A]">{a.title}</h3>
                <p className="text-xs text-[#888]">{a.city}{a.city && a.state ? ", " : ""}{a.state} · {a.units_sqft}</p>
                <p className="mt-1 text-sm font-bold text-emerald-700">{a.price} {a.closed_days && <span className="text-[10px] font-medium text-[#888]">· {a.closed_days}d close</span>}</p>
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={() => setEditing(a)} className="inline-flex items-center gap-1 rounded-md border border-[#e8e0d0] px-2 py-1 text-xs font-semibold text-[#555] hover:bg-[#faf7f2]" data-testid={`edit-acq-${a.id}`}><Edit2 className="h-3 w-3" /> Edit</button>
                  <button onClick={() => togglePublish(a)} className="inline-flex items-center gap-1 rounded-md border border-[#e8e0d0] px-2 py-1 text-xs font-semibold text-[#555] hover:bg-[#faf7f2]" data-testid={`toggle-acq-${a.id}`}>
                    {a.is_published ? <><EyeOff className="h-3 w-3" /> Hide</> : <><Eye className="h-3 w-3" /> Show</>}
                  </button>
                  <button onClick={() => remove(a.id)} className="ml-auto inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid={`delete-acq-${a.id}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="acquisition-editor">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#0A0A0A]">{editing.id ? "Edit" : "Add"} Acquisition</h3>
              <button onClick={() => setEditing(null)} className="rounded-lg p-1 text-[#888] hover:bg-[#faf7f2]"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><label className={labelCls}>Title *</label><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={fieldCls} data-testid="acq-title" /></div>
              <div><label className={labelCls}>City</label><input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} className={fieldCls} /></div>
              <div><label className={labelCls}>State</label><input value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value })} className={fieldCls} /></div>
              <div><label className={labelCls}>Asset Type</label><select value={editing.asset_type} onChange={(e) => setEditing({ ...editing, asset_type: e.target.value })} className={fieldCls}>{ASSET_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
              <div><label className={labelCls}>Size (units/SF)</label><input value={editing.units_sqft} onChange={(e) => setEditing({ ...editing, units_sqft: e.target.value })} className={fieldCls} placeholder="48 Units" /></div>
              <div><label className={labelCls}>Price</label><input value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className={fieldCls} placeholder="$6.2M" /></div>
              <div><label className={labelCls}>Days to Close</label><input value={editing.closed_days} onChange={(e) => setEditing({ ...editing, closed_days: e.target.value })} className={fieldCls} placeholder="14" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Image URL</label><input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} className={fieldCls} placeholder="https://…" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={fieldCls} rows={2} /></div>
              <div><label className={labelCls}>Display Order</label><input type="number" value={editing.display_order} onChange={(e) => setEditing({ ...editing, display_order: parseInt(e.target.value) || 0 })} className={fieldCls} /></div>
              <div className="flex items-end"><label className="flex items-center gap-2 text-sm text-[#555]"><input type="checkbox" checked={editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} className="h-4 w-4 rounded accent-emerald-600" /> Published</label></div>
            </div>
            {editing.image_url && <img src={editing.image_url} alt="preview" className="mt-3 h-36 w-full rounded-lg object-cover" />}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-lg border border-[#e8e0d0] px-4 py-2 text-sm font-semibold text-[#555]">Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60" data-testid="save-acquisition-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
